import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import SelectAddress from '../components/SelectAddress';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify'; 
import LoadingSpinner from './LoadingSpinner';

const ServiceCallFormModal = ({
  isOpen,
  onClose,
  isEditing,
  formData,
  onInputChange,
  onSubmit,
  selectedCompany,
  handleCompanySelect,
  loading,
  setFormData
}) => {

const apiUrl = process.env.REACT_APP_API_URL;
const [employees, setEmployees] = useState([]);
const navigate = useNavigate();
const [NoContract, setNoContract] = useState(0);

const TROUBLE_MAX = 350;
const troubleLen = (formData?.TROUBLE || '').length;
const handleTroubleChange = (e) => {
  const raw = e.target.value || '';
  const clipped = raw.slice(0, TROUBLE_MAX);
  // forward to parent handler, but with clipped value
  onInputChange({
    ...e,
    target: {
      ...e.target,
      name: 'TROUBLE',
      value: clipped,
    },
  });
};

  useEffect(() => {
      const employees = async () => {
        try {
          const response = await axios.get(`${apiUrl}/service_crud.php?employee_list=true`, {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
          });
          const serviceCalls = Array.isArray(response.data) ? response.data : [];
          if (serviceCalls.length === 0) {
            toast.error('No service calls found!', { position: 'top-center' });
          }
          setEmployees(response.data);
        } catch (error) {
          if (error.response && error.response.status === 401) {
            toast.error('Session expired. Please login again.', { position: 'top-center' });
            navigate('/login');
          } else if (error.response && error.response.status === 403) {
            toast.error("Oops!! you don't have access to this area. Speak to the CFO.", {
              position: 'top-center',
            });
          }
        } 
      };

      employees();
  }, [apiUrl, navigate]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value,
    }));
  };


  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-20" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95">
          <div className="fixed inset-0 overflow-y-auto bg-white bg-opacity-70 mt-10">
            <div className="flex min-h-screen items-center justify-center p-4 bg-gray-900/30 backdrop-blur-sm">
              <Dialog.Panel className="mx-auto w-full max-w-2xl overflow-hidden rounded-2xl bg-white/90 backdrop-blur-md shadow-xl ring-1 ring-gray-200">
                {/* Header */}
                {/* Body */}
                <form onSubmit={onSubmit} className="px-6 py-5 space-y-6">

                <div className="flex items-center justify-between border-b border-gray-300 px-6 py-4 bg-[#93c5fd] rounded-lg">
                  <Dialog.Title className="text-xl font-semibold text-gray-900">
                    {isEditing ? 'Edit Service Call' : 'Add New Service Call'}
                  </Dialog.Title>
                <div>
                  {formData.tentative ? ( 
                    <span className="inline-flex items-center rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700 ring-1 ring-red-600/20">
                      Tentative
                    </span>
                  ) : (                 
                    <span className="inline-flex items-center rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700 ring-1 ring-indigo-600/20">
                      Active
                    </span>
                  )}
                </div>
                {/* Optional status pill if you’re using NoContract */}
                <span
                  className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset ${
                    NoContract
                      ? 'bg-amber-50 text-amber-700 ring-amber-600/20'
                      : 'bg-indigo-50 text-indigo-700 ring-indigo-600/20'
                  }`}
                >
                  {NoContract ? 'No Maintenance Contract' : 'Maintenance Contract'}
                </span>                
                </div>
                {/* Tentative checkbox */}
                <div>
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      name="tentative"
                      checked={!!formData.tentative}
                      onChange={handleInputChange}
                      className="h-4 w-4 rounded border-red-400 text-red-600 focus:ring-red-500"
                    />
                    <span className="text-sm font-semibold text-red-600">Tentative</span>
                  </label>                  
                </div>

                {/* Status Description (only if tentative) */}
                {formData.tentative ? (
                  <div>
                    <label className="block text-sm font-medium text-red-700">
                      Status Description
                    </label>
                    <textarea
                      name="status_description"
                      value={formData.status_description || ''}
                      onChange={handleInputChange}
                      rows={3}
                      className="mt-1 block w-full rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-gray-900 focus:border-red-500 focus:ring-red-500"
                      placeholder="Explain why this job is tentative..."
                      required
                    />
                  </div>
                ) : null}

                  {/* Trouble */}
                  <div>
                    <div className="flex items-center justify-between">
                      <label htmlFor="TROUBLE" className="block text-sm font-medium text-gray-700">
                        PO# / Service Ticket Notes <span className="text-red-500">*</span>
                      </label>

                      <span className={`text-xs ${troubleLen >= TROUBLE_MAX ? 'text-red-600' : 'text-gray-500'}`}>
                        {troubleLen}/{TROUBLE_MAX}
                      </span>
                    </div>

                    <textarea
                      name="TROUBLE"
                      id="TROUBLE"
                      value={formData.TROUBLE || ''}
                      onChange={handleTroubleChange}
                      required
                      rows={2}
                      maxLength={TROUBLE_MAX}
                      className="resize-none mt-1 block w-full rounded-lg border border-gray-300 p-3 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                  </div>

                  {/* No Contract toggle */}
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-900">
                    <input
                      type="checkbox"
                      checked={!!NoContract}
                      onChange={(e) => setNoContract(e.target.checked ? 1 : 0)}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    No Maintenance Contract
                  </label>
                  {/* Job Site (search or direct) */}
                  {formData.ADDRESS ? (
                    <div>
                      <label htmlFor="ADDRESS" className="block text-sm font-medium text-gray-700">
                        Job Site <span className="text-red-500">*</span>{' '}
                        <span className="text-xs text-gray-500">(clear to re-search)</span>
                      </label>
                      <input
                        type="text"
                        id="ADDRESS"
                        name="ADDRESS"
                        value={formData.ADDRESS || ''}
                        onChange={onInputChange}
                        required
                        className="mt-1 block w-full rounded-lg border border-gray-300 p-3 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      />
                    </div>
                  ) : (
                    <div>
                      {/* <label className="block text-sm font-medium text-gray-700 mb-1">Select Job Site</label> */}
                      <SelectAddress
                        label="Select Job Site"
                        selectedItem={selectedCompany}
                        NoContract={NoContract}
                        setSelectedItem={handleCompanySelect}
                        apiUrl={`${apiUrl}/service_crud.php`}
                      />
                    </div>
                  )}

                  {/* Customer */}
                  <div>
                    <label htmlFor="CUSTOMER" className="block text-sm font-medium text-gray-700">
                      Customer <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="CUSTOMER"
                      id="CUSTOMER"
                      value={formData.CUSTOMER}
                      onChange={onInputChange}
                      required
                      className="mt-1 block w-full rounded-lg border border-gray-300 p-3 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                  </div>

                  {/* Called by / Phone */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="CALLED_BY" className="block text-sm font-medium text-gray-700">
                        Called By <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="CALLED_BY"
                        id="CALLED_BY"
                        value={formData.CALLED_BY}
                        onChange={onInputChange}
                        required
                        className="mt-1 block w-full rounded-lg border border-gray-300 p-3 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="PHONE" className="block text-sm font-medium text-gray-700">
                        Phone <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="PHONE"
                        id="PHONE"
                        value={formData.PHONE}
                        onChange={onInputChange}
                        required
                        className="mt-1 block w-full rounded-lg border border-gray-300 p-3 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      />
                    </div>
                  </div>

                  {/* Time + Schedule */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div>
                      <label htmlFor="TIME" className="block text-sm font-medium text-gray-700">
                        Time In
                      </label>
                      <input
                        type="time"
                        name="TIME"
                        id="TIME"
                        value={formData.TIME}
                        onChange={onInputChange}
                        className="mt-1 block w-full rounded-lg border border-gray-300 p-2.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="end_time" className="block text-sm font-medium text-gray-700">
                        Time Out
                      </label>
                      <input
                        type="time"
                        name="end_time"
                        id="end_time"
                        value={formData.end_time}
                        onChange={onInputChange}
                        className="mt-1 block w-full rounded-lg border border-gray-300 p-2.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="SCHEDULE_DATE" className="block text-sm font-medium text-gray-700">
                        Schedule Date
                      </label>
                      <input
                        type="date"
                        name="SCHEDULE_DATE"
                        id="SCHEDULE_DATE"
                        value={formData.SCHEDULE_DATE}
                        onChange={onInputChange}
                        className="mt-1 block w-full rounded-lg border border-gray-300 p-2.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      />
                    </div>
                  </div>

                  {/* Techs */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="employeeSelect" className="block text-sm font-medium text-gray-700">
                        Lead Technician
                      </label>
                      <select
                        id="employeeSelect"
                        name="TECH"
                        value={formData.TECH}
                        onChange={onInputChange}
                        className="mt-1 block w-full rounded-lg border border-gray-300 p-2.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      >
                        <option value="">---</option>
                        {employees.map((employee) => (
                          <option key={employee.id} value={`${employee.first_name} ${employee.last_name}`}>
                            {employee.first_name} {employee.last_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="employeeSelect2" className="block text-sm font-medium text-gray-700">
                        Additional Tech
                      </label>
                      <select
                        id="employeeSelect2"
                        name="additional_tech"
                        value={formData.additional_tech}
                        onChange={onInputChange}
                        className="mt-1 block w-full rounded-lg border border-gray-300 p-2.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      >
                        <option value="">---</option>
                        {employees.map((employee) => (
                          <option key={employee.id} value={`${employee.id}`}>
                            {employee.first_name} {employee.last_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <label htmlFor="NOTES" className="block text-sm font-medium text-gray-700">
                      Internal Notes
                    </label>
                    <textarea
                      name="NOTES"
                      id="NOTES"
                      value={formData.NOTES}
                      onChange={onInputChange}
                      rows={4}
                      className="mt-1 block w-full rounded-lg border border-gray-300 p-3 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                  </div>
                </form>

                {loading ? ( <LoadingSpinner children={<p>Loading...</p>} />) : (
                  <>
                    {/* Footer */}
                    <div className="flex items-center justify-end gap-2 border-t border-gray-200 bg-gray-50 px-6 py-4">
                      <button
                        type="button"
                        onClick={onClose}
                        className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold
                                  text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        onClick={onSubmit}
                      >
                        {isEditing ? 'Update' : 'Save'}
                      </button>
                    </div>                  
                  </>
                )}
              </Dialog.Panel>
            </div>
          </div>
        </Transition.Child>
      </Dialog>
    </Transition>
  );
};

export default ServiceCallFormModal;
